import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type MessageTemplate } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { MessageTemplateRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const MESSAGE_TEMPLATES = 'messageTemplates';

const converter = buildConverter<MessageTemplate>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
      lastUsedAt: toTimestampString(data.lastUsedAt) ?? undefined,
    }) as MessageTemplate,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreMessageTemplateRepository implements MessageTemplateRepository {
  async findAll(): Promise<MessageTemplate[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_TEMPLATES),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<MessageTemplate | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, MESSAGE_TEMPLATES, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<MessageTemplate[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_TEMPLATES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByMessageType(projectId: string, messageType: number): Promise<MessageTemplate[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_TEMPLATES),
      where('projectId', '==', projectId),
      where('messageType', '==', messageType),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(template: MessageTemplate): Promise<MessageTemplate> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...template, environment: getCurrentEnvironment() };
    const existing = await this.findById(template.id);
    await setDoc(doc(db, MESSAGE_TEMPLATES, template.id).withConverter(converter), data);
    return { ...data, createdAt: existing?.createdAt ?? template.createdAt, updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, MESSAGE_TEMPLATES, id));
  }

  async duplicate(id: string, newName?: string): Promise<MessageTemplate | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: MessageTemplate = {
      ...original,
      id: `template-${Date.now()}`,
      name: newName ?? `${original.name} (Copy)`,
      parentId: original.id,
      revision: 1,
      updatedAt: new Date().toISOString(),
    };
    return this.save(duplicated);
  }
}

export const createFirestoreMessageTemplateRepository = (): FirestoreMessageTemplateRepository =>
  new FirestoreMessageTemplateRepository();
